import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { WhatsAppIcon } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "5513981197016";
const SERVICE_DEFAULT = "Corte de cabelo";
const WEEKDAY_LETTERS = ["D", "S", "T", "Q", "Q", "S", "S"];
const WEEKDAYS_FULL = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const MONTHS_FULL = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const weekdayShort = (d) =>
  cap(new Intl.DateTimeFormat("pt-BR", { weekday: "short" }).format(d).replace(".", ""));
const monthShort = (d) =>
  new Intl.DateTimeFormat("pt-BR", { month: "short" }).format(d).replace(".", "");
const monthLabel = (cursor) =>
  `${cap(new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(cursor))} ${cursor.getFullYear()}`;
export const fullDate = (key) => {
  const d = new Date(`${key}T12:00:00`);
  return `${WEEKDAYS_FULL[d.getDay()]}, ${d.getDate()} de ${MONTHS_FULL[d.getMonth()]}`;
};

export const maskPhone = (value) => {
  const n = value.replace(/\D/g, "").slice(0, 11);
  if (n.length === 0) return "";
  if (n.length <= 2) return `(${n}`;
  if (n.length <= 6) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
  if (n.length <= 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
  return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
};

function buildCells(cursor) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const cells = Array(firstWeekday).fill(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const stepMotion = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -14 },
  transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
};

export default function Booking({ onBack }) {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [availability, setAvailability] = useState({ slots: [], booked: [] });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const cells = useMemo(() => buildCells(cursor), [cursor]);
  const minMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const canGoPrev = cursor > minMonth;
  const todayKey = toKey(new Date());
  const nowHour = new Date().getHours();

  useEffect(() => {
    if (!date) return undefined;
    let active = true;
    setLoadingSlots(true);
    axios
      .get(`${API}/bookings/availability`, { params: { date } })
      .then((res) => {
        if (active) setAvailability(res.data);
      })
      .catch(() => toast.error("Não foi possível carregar os horários."))
      .finally(() => {
        if (active) setLoadingSlots(false);
      });
    return () => {
      active = false;
    };
  }, [date]);

  const hoursGrid = useMemo(() => {
    if (!availability.slots.length) return [];
    const last = Number(availability.slots[availability.slots.length - 1].slice(0, 2));
    const hours = [];
    for (let h = 11; h <= last; h++) hours.push(h);
    return hours;
  }, [availability]);

  const bookedName = (slot) =>
    availability.booked.find((b) => b.time === slot)?.name || null;

  const slotTaken = (slot) =>
    Boolean(bookedName(slot)) ||
    (date === todayKey && Number(slot.slice(0, 2)) <= nowHour);

  const selectDay = (d) => {
    setDate(toKey(d));
    setTime(null);
  };

  const submit = async () => {
    if (name.trim().length < 2) {
      toast.error("Informe seu nome completo.");
      return;
    }
    if (phone.replace(/\D/g, "").length < 10) {
      toast.error("Informe um WhatsApp válido com DDD.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        name: name.trim(),
        phone,
        date,
        time,
        service: SERVICE_DEFAULT,
      });
      setConfirmed(data.booking);
      setStep(3);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Não foi possível confirmar. Tente novamente."
      );
      if (err.response?.status === 409) {
        try {
          const res = await axios.get(`${API}/bookings/availability`, { params: { date } });
          setAvailability(res.data);
        } catch {
          /* silencioso */
        }
        setTime(null);
        setStep(1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(1);
    setDate(null);
    setTime(null);
    setName("");
    setPhone("");
    setConfirmed(null);
  };

  const cancelUrl = confirmed
    ? `${window.location.origin}/?cancelar=${confirmed.cancel_token}`
    : "";

  const waUrl = confirmed
    ? (() => {
        const d = new Date(`${confirmed.date}T12:00:00`);
        const msg = `Olá! Agendei pelo site: ${weekdayShort(d)}, ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} às ${confirmed.time}. Nome: ${confirmed.name}. Como fica o pagamento?\nSe precisar cancelar: ${cancelUrl}`;
        return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
      })()
    : "#";

  const chipDate = date
    ? (() => {
        const d = new Date(`${date}T12:00:00`);
        return `${weekdayShort(d)}, ${d.getDate()} ${monthShort(d)} · ${time}`;
      })()
    : "";

  return (
    <motion.section
      key="agenda-page"
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="px-6 py-10"
    >
      <button
        type="button"
        onClick={onBack}
        className="mb-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone-500 transition-colors duration-300 hover:text-amber-300"
        data-testid="booking-back-home"
      >
        ← Voltar ao início
      </button>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="mb-10 text-center text-[10px] font-semibold uppercase tracking-[0.45em] text-amber-500/70"
      >
        Agenda Online
      </motion.p>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="step-1" {...stepMotion}>
            <h2 className="font-display text-2xl font-semibold text-amber-100">Escolha o dia</h2>

            <div className="mt-6" data-testid="booking-date-picker">
              <div className="mb-4 flex items-center justify-between">
                <p className="font-medium text-amber-400" data-testid="booking-month-label">
                  {monthLabel(cursor)}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={!canGoPrev}
                    onClick={() =>
                      setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))
                    }
                    className="rounded-full p-1.5 text-stone-400 transition-colors duration-300 hover:text-amber-300 disabled:opacity-30"
                    aria-label="Mês anterior"
                    data-testid="booking-prev-month"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))
                    }
                    className="rounded-full p-1.5 text-stone-400 transition-colors duration-300 hover:text-amber-300"
                    aria-label="Próximo mês"
                    data-testid="booking-next-month"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-y-1 text-center">
                {WEEKDAY_LETTERS.map((l, i) => (
                  <span key={i} className="pb-2 text-[11px] font-medium text-stone-500">
                    {l}
                  </span>
                ))}
                {cells.map((d, i) => {
                  if (!d) return <span key={`empty-${i}`} />;
                  const key = toKey(d);
                  const disabled = d < today || d.getDay() === 0 || d.getDay() === 1;
                  const selected = key === date;
                  const isToday = key === todayKey;
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={disabled}
                      onClick={() => selectDay(d)}
                      className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full text-sm transition-[background-color,color,box-shadow,transform] duration-300 ${
                        selected
                          ? "bg-gradient-to-b from-[#E9C767] to-[#B38728] font-bold text-black shadow-[0_4px_16px_rgba(212,175,55,0.4)]"
                          : disabled
                            ? "cursor-not-allowed text-stone-700"
                            : isToday
                              ? "text-amber-300 ring-1 ring-amber-400/70 hover:bg-amber-400/10"
                              : "text-stone-300 hover:bg-amber-400/10 hover:text-amber-200"
                      }`}
                      data-testid={`booking-date-${key}`}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>

            {date && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8"
              >
                <p className="mb-4 text-sm">
                  <span className="font-display font-semibold text-amber-400">Horários</span>
                  <span className="text-stone-400">
                    {" · "}
                    {(() => {
                      const d = new Date(`${date}T12:00:00`);
                      return `${weekdayShort(d)}, ${d.getDate()} de ${monthShort(d)}`;
                    })()}
                  </span>
                </p>
                {loadingSlots ? (
                  <div className="grid grid-cols-3 gap-3" data-testid="booking-slots-loading">
                    {Array.from({ length: 9 }).map((_, i) => (
                      <div
                        key={i}
                        className="h-12 animate-pulse rounded-xl border border-amber-500/10 bg-white/[0.03]"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {hoursGrid.map((h) => {
                      if (h === 14) {
                        return (
                          <div
                            key="almoco"
                            className="flex items-center justify-center rounded-xl border border-stone-800/60 py-3 text-sm italic text-stone-600"
                            data-testid="booking-slot-lunch"
                          >
                            Almoço
                          </div>
                        );
                      }
                      const slot = `${String(h).padStart(2, "0")}:00`;
                      const bookedBy = bookedName(slot);
                      const taken = slotTaken(slot);
                      const active = slot === time;
                      return (
                        <button
                          key={slot}
                          type="button"
                          disabled={taken}
                          onClick={() => setTime(slot)}
                          className={`rounded-xl border py-3 text-sm transition-[background-color,border-color,color,box-shadow,transform] duration-300 ${
                            bookedBy
                              ? "cursor-not-allowed border-amber-500/15 bg-white/[0.03]"
                              : taken
                                ? "cursor-not-allowed border-stone-800/60 text-stone-700 line-through"
                                : active
                                  ? "border-transparent bg-gradient-to-b from-[#E9C767] to-[#B38728] font-bold text-black shadow-[0_4px_16px_rgba(212,175,55,0.4)]"
                                  : "border-amber-500/25 text-amber-100 hover:-translate-y-0.5 hover:border-amber-400/60 hover:bg-amber-400/5"
                          }`}
                          data-testid={`booking-time-slot-${slot.replace(":", "-")}`}
                        >
                          {bookedBy ? (
                            <span className="block leading-tight">
                              <span className="block text-[11px] text-stone-600 line-through">
                                {slot}
                              </span>
                              <span className="block truncate px-1 text-xs font-semibold text-amber-500/80">
                                {bookedBy}
                              </span>
                            </span>
                          ) : (
                            slot
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                <button
                  type="button"
                  disabled={!time}
                  onClick={() => setStep(2)}
                  className="gold-btn mt-8 w-full"
                  data-testid="booking-continue-button"
                >
                  Continuar
                </button>
              </motion.div>
            )}
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="step-2" {...stepMotion}>
            <h2 className="font-display text-2xl font-semibold text-amber-100">Seus dados</h2>

            <button
              type="button"
              onClick={() => setStep(1)}
              className="mt-5 inline-flex items-center rounded-full border border-amber-500/30 px-4 py-2 text-xs tracking-wide text-amber-200 transition-colors duration-300 hover:border-amber-400/60"
              data-testid="booking-selected-slot"
            >
              {chipDate}
            </button>

            <div className="mt-7 space-y-5">
              <label className="block">
                <span className="mb-2 block text-xs text-stone-500">Nome</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Seu nome completo"
                  className="input-dark"
                  data-testid="booking-input-name"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-xs text-stone-500">Telefone (WhatsApp)</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(maskPhone(e.target.value))}
                  placeholder="(13) 99999-9999"
                  className="input-dark"
                  data-testid="booking-input-phone"
                />
              </label>
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="gold-btn mt-8 w-full"
              data-testid="booking-submit-button"
            >
              {submitting ? "Confirmando..." : "Confirmar agendamento"}
            </button>
            <p className="mt-4 text-center text-xs text-stone-500">
              Em seguida você fala direto com o barbeiro.
            </p>
          </motion.div>
        )}

        {step === 3 && confirmed && (
          <motion.div key="step-3" {...stepMotion} className="text-center" data-testid="booking-confirmation">
            <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border-2 border-amber-400/80 shadow-[0_0_40px_rgba(212,175,55,0.2)]">
              <Check size={44} strokeWidth={2} className="text-amber-400" />
            </div>
            <h2 className="mt-6 font-display text-3xl text-amber-100">
              Agendamento
              <br />
              <span className="gold-text italic">confirmado!</span>
            </h2>

            <dl className="mt-8 divide-y divide-amber-500/10 rounded-2xl border border-amber-500/20 bg-white/[0.02] text-left">
              {[
                ["Data", fullDate(confirmed.date)],
                ["Horário", confirmed.time],
                ["Serviço", confirmed.service],
                ["Cliente", confirmed.name],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-4 px-5 py-4">
                  <dt className="text-xs text-stone-500">{label}</dt>
                  <dd className="text-right text-sm font-medium text-amber-100">{value}</dd>
                </div>
              ))}
            </dl>

            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="wa-btn mt-8 w-full"
              data-testid="whatsapp-confirm-button"
            >
              <WhatsAppIcon size={20} /> Combinar no WhatsApp
            </a>
            <p className="mt-4 text-xs text-stone-500">
              Pagamento combinado direto com o barbeiro
            </p>
            <a
              href={cancelUrl}
              className="mt-3 inline-block text-xs text-red-400/80 underline-offset-4 transition-colors duration-300 hover:text-red-300 hover:underline"
              data-testid="confirmation-cancel-link"
            >
              Precisa cancelar? Toque aqui
            </a>
            <div>
              <button
                type="button"
                onClick={reset}
                className="mt-6 text-xs uppercase tracking-[0.2em] text-amber-500/70 underline-offset-4 transition-colors duration-300 hover:text-amber-300 hover:underline"
                data-testid="booking-new-button"
              >
                Fazer outro agendamento
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}
